import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import type { NetWorthPeriodItem } from '@/api/types';

type SortField = 'period' | 'assets' | 'debts' | 'netWorth' | 'change' | 'changePercent';
type SortDirection = 'asc' | 'desc';

/**
 * Extended NetWorthPeriodItem with optional projection flag.
 * Used to display both historical and projected data in the table.
 */
export interface NetWorthTableItem extends NetWorthPeriodItem {
    /** Whether this item is a projected value (true) or historical (false) */
    isProjected?: boolean;
}

interface NetWorthTableProps {
    /** Array of period items for table display (can include projected items) */
    items: NetWorthTableItem[];
    /** Display currency */
    currency: string;
}

/**
 * Get change color based on value.
 * Positive is green, negative is red, zero/null is neutral.
 */
function getChangeColorClass(value: number | null): string {
    if (value === null) return '';
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
}

/**
 * Format percentage with sign.
 */
function formatPercentage(value: number | null): string {
    if (value === null) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
}

/**
 * Sort indicator icon component.
 */
function SortIcon({ field, currentField, direction }: { field: SortField; currentField: SortField; direction: SortDirection }) {
    if (field !== currentField) {
        return (
            <span className="ml-1 inline-flex flex-col opacity-30">
                <ChevronUpIcon className="h-3 w-3 -mb-1" />
                <ChevronDownIcon className="h-3 w-3" />
            </span>
        );
    }
    return direction === 'asc' ? (
        <ChevronUpIcon className="h-4 w-4 ml-1 inline" />
    ) : (
        <ChevronDownIcon className="h-4 w-4 ml-1 inline" />
    );
}

/**
 * Net Worth table component displaying historical and projected data in tabular format.
 * Columns: Period | Assets | Debts | Net Worth | Change | Change %
 * Features:
 * - Sortable columns with sort indicators
 * - Projected rows with distinct styling (italic text, subtle background tint)
 * - "(Projected)" suffix on projected period labels
 */
export function NetWorthTable({ items, currency }: NetWorthTableProps) {
    const [sortField, setSortField] = useState<SortField>('period');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Sort data
    const sortedItems = useMemo(() => {
        const sorted = [...items];
        sorted.sort((a, b) => {
            let aVal: number | string | null;
            let bVal: number | string | null;

            switch (sortField) {
                case 'period':
                    aVal = a.periodEnd;
                    bVal = b.periodEnd;
                    break;
                case 'assets':
                    aVal = a.assetsValue;
                    bVal = b.assetsValue;
                    break;
                case 'debts':
                    aVal = a.debtsValue;
                    bVal = b.debtsValue;
                    break;
                case 'netWorth':
                    aVal = a.netWorthValue;
                    bVal = b.netWorthValue;
                    break;
                case 'change':
                    aVal = a.netWorthChange;
                    bVal = b.netWorthChange;
                    break;
                case 'changePercent':
                    aVal = a.netWorthChangePercent;
                    bVal = b.netWorthChangePercent;
                    break;
                default:
                    aVal = a.periodEnd;
                    bVal = b.periodEnd;
            }

            // Handle nulls
            if (aVal === null && bVal === null) return 0;
            if (aVal === null) return sortDirection === 'asc' ? 1 : -1;
            if (bVal === null) return sortDirection === 'asc' ? -1 : 1;

            // Compare
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [items, sortField, sortDirection]);

    // Handle column header click
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Header button style
    const headerButtonClass = "flex items-center cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors";

    if (items.length === 0) {
        return (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No data available
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={headerButtonClass}
                                onClick={() => handleSort('period')}
                            >
                                Period
                                <SortIcon field="period" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('netWorth')}
                            >
                                Net Worth
                                <SortIcon field="netWorth" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="hidden sm:table-cell text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('assets')}
                            >
                                Assets
                                <SortIcon field="assets" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="hidden sm:table-cell text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('debts')}
                            >
                                Debts
                                <SortIcon field="debts" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('change')}
                            >
                                Change
                                <SortIcon field="change" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('changePercent')}
                            >
                                Change %
                                <SortIcon field="changePercent" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedItems.map((item, index) => {
                        const isProjected = item.isProjected === true;

                        // Base row classes
                        const baseRowClasses = 'border-b border-gray-100 dark:border-gray-800';

                        // Projected rows get italic styling and subtle blue tint background
                        // Non-projected rows get alternating gray background
                        const rowBgClasses = isProjected
                            ? 'bg-blue-50/30 dark:bg-blue-900/10'
                            : index % 2 === 0
                                ? 'bg-gray-50/50 dark:bg-gray-800/30'
                                : '';

                        // Projected rows get italic text styling
                        const projectedTextClass = isProjected ? 'italic' : '';

                        return (
                            <tr
                                key={item.periodEnd}
                                className={`${baseRowClasses} ${rowBgClasses} ${projectedTextClass}`}
                            >
                                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                    {item.periodLabel}{isProjected && ' (Projected)'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <MoneyDisplay
                                        value={item.netWorthValue}
                                        currency={currency}
                                        size="sm"
                                        className="font-semibold text-gray-900 dark:text-white"
                                    />
                                </td>
                                <td className="hidden sm:table-cell px-4 py-3 text-right">
                                    <MoneyDisplay
                                        value={item.assetsValue}
                                        currency={currency}
                                        size="sm"
                                        className="text-gray-900 dark:text-white"
                                    />
                                </td>
                                <td className="hidden sm:table-cell px-4 py-3 text-right">
                                    <MoneyDisplay
                                        value={item.debtsValue}
                                        currency={currency}
                                        size="sm"
                                        className="text-gray-900 dark:text-white"
                                    />
                                </td>
                                <td className={`px-4 py-3 text-right ${getChangeColorClass(item.netWorthChange)}`}>
                                    {item.netWorthChange !== null ? (
                                        <MoneyDisplay
                                            value={item.netWorthChange}
                                            currency={currency}
                                            size="sm"
                                            showSign
                                            className="inline"
                                        />
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                                <td className={`px-4 py-3 text-right ${getChangeColorClass(item.netWorthChangePercent)}`}>
                                    {formatPercentage(item.netWorthChangePercent)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
